/**
 * A variant's own details (Bagz Phones, 31 Oct 2026).
 *
 * One phone comes in 256GB, 512GB, 1TB and 2TB, in three colours. Each of
 * those twelve options can have:
 *   - its own BUYING price  — `products.variants[i].costPrice` (a 2TB costs
 *     the shop more than a 256GB, so its profit is different);
 *   - its own manufacturer code — `products.variants[i].manufacturerCode`
 *     (the factory part number of that colour and storage);
 *   - its own specs — `products.variants[i].specs`, keyed by the electronics
 *     spec name ("model", "battery", "simType", …), each one a value that
 *     replaces the product's own for that option.
 *
 * Absent means "the same as the product": an option with no buying price
 * costs what the product costs, an option with no SIM type has the product's.
 *
 * Pure (no Convex, no `@/` imports): the till, the website, the product form
 * and the Convex mutations all read it. Tests: ./variantDetails.test.ts.
 */

import { sanitizeVariantId } from "../utils/variantPricing";

export type VariantSpecs = Record<string, string>;

/** What a saved variant carries beyond its id, name, price and axes. */
export interface VariantDetails {
  /** The option's own buying price; absent = the product's Cost Price. */
  costPrice?: number;
  /** The factory / supplier code of this one option. */
  manufacturerCode?: string;
  /** Spec values that differ from the product's, by spec key. */
  specs?: VariantSpecs;
}

/**
 * What a product save sends for one variant. A field that is not sent
 * (undefined) leaves the saved value alone — an old browser tab, the till's
 * quick price edit or a product opened from a short list row never wipes a
 * code it did not show. A field sent empty ("" / {} / 0) clears it.
 */
export interface VariantDetailsInput {
  ownCostPrice?: number;
  manufacturerCode?: string;
  specs?: Record<string, string>;
}

const MAX_SPECS = 24;
const MAX_SPEC_VALUE = 120;
const MAX_CODE = 80;

/**
 * A spec key Convex will keep: ASCII, not empty, never starting with "$" or
 * "_" (Convex refuses those object keys). Letters, digits, ".", "-" and "_".
 */
export function isSpecKey(key: string): boolean {
  return /^[A-Za-z0-9][A-Za-z0-9_.-]{0,39}$/.test(key);
}

/** A spec value as one line of text; a list ("Nano SIM", "eSIM") joins with ", ". */
export function specText(value: unknown): string {
  if (Array.isArray(value)) {
    const seen = new Set<string>();
    const parts: string[] = [];
    for (const item of value) {
      const t = specText(item);
      if (t && !seen.has(t.toLowerCase())) {
        seen.add(t.toLowerCase());
        parts.push(t);
      }
    }
    return parts.join(", ");
  }
  if (typeof value === "string") return value.trim().replace(/\s+/g, " ");
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return "";
}

/** Spec overrides as they are stored: safe keys, trimmed one-line values, no blanks. */
export function cleanVariantSpecs(input: unknown): VariantSpecs {
  const out: VariantSpecs = {};
  if (!input || typeof input !== "object" || Array.isArray(input)) return out;
  for (const [rawKey, rawValue] of Object.entries(input as Record<string, unknown>)) {
    if (Object.keys(out).length >= MAX_SPECS) break;
    const key = String(rawKey).trim();
    if (!isSpecKey(key)) continue;
    const value = specText(rawValue).slice(0, MAX_SPEC_VALUE);
    if (value) out[key] = value;
  }
  return out;
}

/** A manufacturer code as stored: trimmed, single spaces, at most 80 characters. */
export function cleanManufacturerCode(input: unknown): string {
  return typeof input === "string" ? input.trim().replace(/\s+/g, " ").slice(0, MAX_CODE) : "";
}

/** The product's top-level variant with this id (same matching as the price resolver). */
export function storedVariant(product: any, variantId: string | undefined | null): any | undefined {
  if (!variantId) return undefined;
  const list: any[] = Array.isArray(product?.variants) ? product.variants : [];
  if (list.length === 0) return undefined;
  const target = sanitizeVariantId(String(variantId).trim());
  return list.find((v) => v?.id && sanitizeVariantId(String(v.id).trim()) === target);
}

const positive = (n: unknown): number | undefined =>
  typeof n === "number" && Number.isFinite(n) && n > 0 ? n : undefined;

/** The option's own buying price, when it has one. */
export function ownVariantCost(product: any, variantId: string | undefined | null): number | undefined {
  return positive(storedVariant(product, variantId)?.costPrice);
}

/**
 * What one unit of this option cost the shop: its own buying price, else the
 * product's Cost Price. Undefined when neither is set — a sale then falls
 * back to the cost on the stock batch it sold from, as it always has.
 */
export function variantCostPrice(product: any, variantId: string | undefined | null): number | undefined {
  return ownVariantCost(product, variantId) ?? positive(Number(product?.costPrice));
}

/** The option's own manufacturer code, else undefined (the product's code is the caller's fallback). */
export function variantManufacturerCode(product: any, variantId: string | undefined | null): string | undefined {
  const code = cleanManufacturerCode(storedVariant(product, variantId)?.manufacturerCode);
  return code || undefined;
}

/**
 * The details a variant keeps after a save: what was sent, and for whatever
 * was NOT sent, what it already had. Only fields with a value are returned,
 * so the result spreads straight into the stored variant.
 */
export function mergeVariantDetails(stored: any | undefined, incoming: VariantDetailsInput | undefined): VariantDetails {
  const out: VariantDetails = {};
  const inc = incoming ?? {};

  const cost = inc.ownCostPrice !== undefined ? positive(inc.ownCostPrice) : positive(stored?.costPrice);
  if (cost !== undefined) out.costPrice = cost;

  const code =
    inc.manufacturerCode !== undefined
      ? cleanManufacturerCode(inc.manufacturerCode)
      : cleanManufacturerCode(stored?.manufacturerCode);
  if (code) out.manufacturerCode = code;

  const specs = inc.specs !== undefined ? cleanVariantSpecs(inc.specs) : cleanVariantSpecs(stored?.specs);
  if (Object.keys(specs).length > 0) out.specs = specs;

  return out;
}

/**
 * The buying price a variant's stock batches should carry after a save: its
 * own price when it has one, else what the form sent (the product's cost).
 * Null when there is nothing real to write — a batch's cost is never wiped to 0.
 */
export function batchCostForVariant(ownCost: number | undefined, sentCost: number | undefined): number | null {
  return positive(ownCost) ?? positive(sentCost) ?? null;
}

/**
 * The variant buying prices that go with a new product Cost Price — the same
 * follow rule the selling prices have (convex/helpers/productHelpers.ts
 * variantPricesForNewBase): an option costed the same as the OLD product cost
 * follows the product (its own price is dropped); only an option deliberately
 * costed differently keeps its own. Returns the new list, or undefined when
 * nothing changes.
 */
export function variantCostsForNewCost(product: any, newCost: number): any[] | undefined {
  const list: any[] = Array.isArray(product?.variants) ? product.variants : [];
  if (list.length === 0) return undefined;
  const oldCost = Number(product?.costPrice) || 0;
  if (oldCost === newCost) return undefined;
  let changed = false;
  const next = list.map((v) => {
    const own = positive(v?.costPrice);
    if (own === undefined || own !== oldCost) return v;
    changed = true;
    const { costPrice: _drop, ...rest } = v;
    return rest;
  });
  return changed ? next : undefined;
}

/**
 * Variants as a visitor may see them: the shop's buying price and its
 * supplier code are its own business (lib/config/productAccess.ts lists the
 * product-level ones as back office). Everything else — specs, price, axes —
 * stays.
 */
export function publicVariants<T>(variants: T): T {
  if (!Array.isArray(variants)) return variants;
  return variants.map((v: any) => {
    if (!v || typeof v !== "object" || (!("costPrice" in v) && !("manufacturerCode" in v))) return v;
    const { costPrice: _cost, manufacturerCode: _code, ...rest } = v;
    return rest;
  }) as unknown as T;
}

/** An option's own details as the product form holds them. */
export interface VariantFormDetails {
  /** Its own buying price; 0 = the product's Cost Price (blank box). */
  costPrice?: number;
  manufacturerCode?: string;
  specs?: VariantSpecs;
}

/**
 * The product form's variants with each option's saved details loaded.
 *
 * A buying price equal to the product's loads blank (0), so it follows the
 * product's Cost Price — the same rule the selling prices have. When the
 * product was opened without its variants (a short list row), nothing is
 * loaded, nothing is sent, and the save keeps what is saved.
 */
export function withStoredVariantDetails<T extends { id: string }>(
  list: T[],
  storedVariants: unknown,
  productCost: unknown,
): Array<T & VariantFormDetails> {
  if (!Array.isArray(storedVariants)) return list;
  const base = Number(productCost) || 0;
  return list.map((v) => {
    const stored = storedVariant({ variants: storedVariants }, v.id);
    const own = positive(stored?.costPrice);
    return {
      ...v,
      costPrice: own !== undefined && own !== base ? own : 0,
      manufacturerCode: cleanManufacturerCode(stored?.manufacturerCode),
      specs: cleanVariantSpecs(stored?.specs),
    };
  });
}

/**
 * What the product form sends for one option's details. Only what the form
 * holds is sent (a field it never loaded stays undefined, and the server
 * keeps the saved value); a cleared box is sent empty, which clears it.
 */
export function variantDetailsForSave(v: VariantFormDetails): VariantDetailsInput {
  const out: VariantDetailsInput = {};
  if (typeof v.costPrice === "number") out.ownCostPrice = positive(v.costPrice) ?? 0;
  if (typeof v.manufacturerCode === "string") out.manufacturerCode = cleanManufacturerCode(v.manufacturerCode);
  if (v.specs && typeof v.specs === "object") out.specs = cleanVariantSpecs(v.specs);
  return out;
}

/**
 * Words a search should find a product by, from its variants' own details:
 * each option's manufacturer code and spec values. (The till and the
 * Products page search these as well as the product's own code.)
 */
export function variantSearchTerms(variants: unknown): string[] {
  if (!Array.isArray(variants)) return [];
  const out: string[] = [];
  for (const v of variants as any[]) {
    const code = cleanManufacturerCode(v?.manufacturerCode);
    if (code) out.push(code);
    const specs = cleanVariantSpecs(v?.specs);
    for (const value of Object.values(specs)) out.push(value);
  }
  return out;
}
